import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { Search, MessageSquare, X } from "lucide-react";
import NotificationBell from "./NotificationBell";
import ThemeToggle from "./ThemeToggle";

export default function Topbar({ title, subtitle, right, breadcrumb }) {
  const { currentWorkspaceId } = useWorkspace();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2 || !currentWorkspaceId) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      api.get("/search", { params: { q, workspace_id: currentWorkspaceId } })
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q, currentWorkspaceId]);

  const toggleSearch = () => {
    setSearchExpanded((v) => {
      const next = !v;
      if (next) setTimeout(() => inputRef.current?.focus(), 50);
      else { setQ(""); setOpen(false); }
      return next;
    });
  };

  return (
    <div
      data-testid="topbar"
      className="flex items-center justify-between gap-2 sm:gap-4 border-b border-zinc-800 bg-[#0B0B0E] px-3 sm:px-6 py-3 sticky top-0 z-20"
    >
      <div className={`min-w-0 ${searchExpanded ? "hidden sm:block" : ""}`}>
        {breadcrumb && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
            {breadcrumb}
          </div>
        )}
        <h1 className="text-lg font-bold tracking-tight text-white truncate" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-xs text-zinc-500 truncate">{subtitle}</p>}
      </div>

      <div className={`flex items-center gap-1.5 sm:gap-2 ${searchExpanded ? "flex-1 sm:flex-initial" : ""}`}>
        {/* Search: full input on sm+, collapsible icon below sm */}
        <div className={`relative ${searchExpanded ? "flex-1 sm:flex-initial" : ""}`}>
          {/* Mobile-only icon trigger when collapsed */}
          {!searchExpanded && (
            <button
              onClick={toggleSearch}
              data-testid="global-search-toggle"
              aria-label="Open search"
              aria-expanded={searchExpanded}
              className="sm:hidden flex items-center justify-center w-9 h-9 bg-[#141417] border border-zinc-800 hover:border-zinc-600 rounded-md text-zinc-300"
            >
              <Search className="w-4 h-4" />
            </button>
          )}
          {/* Input is always rendered on sm+; on mobile it appears only when expanded */}
          <div className={`${searchExpanded ? "flex" : "hidden sm:flex"} items-center relative`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => results && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder="Search…"
              data-testid="global-search-input"
              className="w-full sm:w-56 md:w-72 bg-[#141417] border border-zinc-800 hover:border-zinc-700 focus:border-[#D4A968] text-sm text-white placeholder:text-zinc-600 rounded-md pl-9 pr-9 sm:pr-3 py-2 outline-none transition-colors"
            />
            {searchExpanded && (
              <button
                onClick={toggleSearch}
                aria-label="Close search"
                data-testid="global-search-close"
                className="sm:hidden absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {open && results && (
            <div className="fixed left-2 right-2 top-14 md:absolute md:left-auto md:right-0 md:top-full md:mt-1 w-auto md:w-[28rem] max-w-full md:max-w-[28rem] max-h-[24rem] overflow-y-auto bg-[#141417] border border-zinc-800 rounded-md shadow-2xl z-30" data-testid="search-results">
              {results.projects.length === 0 && results.tasks.length === 0 && results.messages.length === 0 && (
                <div className="p-4 text-xs text-zinc-500">No results</div>
              )}
              {results.projects.length > 0 && (
                <div className="border-b border-zinc-800">
                  <div className="px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500 bg-[#0B0B0E]">Projects</div>
                  {results.projects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { nav(`/projects/${p.id}`); setOpen(false); setQ(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#1B1B1F] text-sm text-white"
                    >
                      <div className="text-xs font-mono text-zinc-500">{p.code}</div>
                      <div>{p.name}</div>
                    </button>
                  ))}
                </div>
              )}
              {results.tasks.length > 0 && (
                <div className="border-b border-zinc-800">
                  <div className="px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500 bg-[#0B0B0E]">Tasks</div>
                  {results.tasks.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { nav(`/projects/${t.project_id}?task=${t.id}`); setOpen(false); setQ(""); }}
                      className="w-full text-left px-3 py-2 hover:bg-[#1B1B1F] text-sm text-zinc-200"
                    >
                      {t.title}
                    </button>
                  ))}
                </div>
              )}
              {results.messages.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500 bg-[#0B0B0E]">Messages</div>
                  {results.messages.map((m) => (
                    <div key={m.id} className="px-3 py-2 text-xs text-zinc-300">{m.text.slice(0, 100)}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          onClick={() => nav("/chat")}
          data-testid="chat-button"
          aria-label="Open chat"
          title="Chat"
          className="flex items-center gap-1.5 bg-[#141417] border border-zinc-800 hover:border-zinc-600 rounded-md text-sm text-zinc-300 hover:text-white transition-colors w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-2 justify-center"
        >
          <MessageSquare className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
          <span className="hidden sm:inline">Chat</span>
        </button>

        <NotificationBell />

        <ThemeToggle />

        {right}
      </div>
    </div>
  );
}
